import React, { useState, useEffect, useCallback } from 'react'
import './App.css'
import './Home.css' // ticket card + grid styles (loaded on top of the Feepage stylesheet by Home)
import axios from 'axios'
import TopBarProgress from 'react-topbar-progress-indicator'
import { API_URL } from './config'
import { BiSolidLocationPlus } from 'react-icons/bi'
import { BsArrowUpRight, BsClock, BsTicketPerforated } from 'react-icons/bs'

const WS = `${API_URL}/apps/kscanner/webservices.php`

// Descriptions come back as HTML; card previews only need plain text.
const plainText = (html = '', max = 90) => {
  const clean = String(html)
    .replace(/<[^>]*>/g, '')
    .replace(/\s+/g, ' ')
    .trim()
  return clean.length > max ? clean.slice(0, max).trimEnd() + '…' : clean
}

const hidePoster = (e) => {
  e.currentTarget.style.display = 'none'
}

// Cursor-follow spotlight on cards.
const spot = (e) => {
  const r = e.currentTarget.getBoundingClientRect()
  e.currentTarget.style.setProperty('--mx', `${e.clientX - r.left}px`)
  e.currentTarget.style.setProperty('--my', `${e.clientY - r.top}px`)
}

const IndexedEvents = ({ limit = null }) => {
  const [province] = useState('')
  const [currentEvent, setCurrentEvent] = useState([])
  const [userData, setUserData] = useState({
    email: '',
    phone: '',
    customername: '',
  })

  const [loading, setLoading] = useState({
    loadBarProgress: true,
    error: null,
  })

  const customerid = localStorage.getItem('customerid')

  const fetchEvents = useCallback(
    (prov) => {
      const queryProv = prov ? `&province=${prov}` : ''
      const apiUrlCur = `${WS}?query=INDEXED-EVENTS${queryProv}`

      fetch(apiUrlCur)
        .then((res) => res.json())
        .then((result) => {
          let events = Array.isArray(result) ? result : []

          if (limit) {
            events = events.slice(0, limit)
          }

          setCurrentEvent(events)
          setLoading((prev) => ({
            ...prev,
            loadBarProgress: false,
          }))
        })
        .catch((err) =>
          setLoading((prev) => ({
            ...prev,
            error: err,
            loadBarProgress: false,
          })),
        )
    },
    [limit],
  )

  useEffect(() => {
    if (customerid) {
      axios
        .get(`${WS}?query=CUSTOMERDETAILS&accountid=${customerid}`, {
          withCredentials: true,
        })
        .then((response) => {
          setUserData({
            email: response.data.email,
            phone: response.data.phone,
            customername: response.data.customername,
          })
        })
        .catch((err) => console.error('User fetch error:', err))
    }

    fetchEvents(province)
  }, [province, fetchEvents, customerid])

  const date = new Date()

  const fulldate = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(
    2,
    '0',
  )}-${String(date.getDate()).padStart(2, '0')}`

  if (loading.error) {
    return (
      <div className="fp-state" role="alert">
        <BsTicketPerforated className="fp-state__icon" aria-hidden="true" />
        <h3>We couldn't load external events</h3>
        <p>Check your connection and try again.</p>
      </div>
    )
  }

  const visibleEvents = currentEvent.filter(
    (item) => !(item.balance < 3.5 && item.event_day > fulldate),
  )

  return (
    <section className="fp-indexed">
      <div className="fp-head">
        <h2 className="fp-h2">
          More events from <span>around the web.</span>
        </h2>

        <div>
          <p>Discover events listed on Karamale but ticketed through external platforms.</p>

          {!loading.loadBarProgress && visibleEvents.length > 0 && (
            <span className="fp-count">
              {visibleEvents.length} {visibleEvents.length === 1 ? 'event' : 'events'}
            </span>
          )}
        </div>
      </div>

      {loading.loadBarProgress && (
        <>
          <TopBarProgress />

          <div className="fp-egrid" aria-busy="true" aria-label="Loading external events">
            {Array.from({ length: 4 }).map((_, i) => (
              <div className="fp-skel" key={i}>
                <div className="fp-skel__img" />
                <div className="fp-skel__body">
                  <div className="fp-skel__line" />
                  <div className="fp-skel__line" />
                  <div className="fp-skel__line" />
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      {!loading.loadBarProgress && visibleEvents.length === 0 && (
        <div className="fp-state">
          <BsTicketPerforated className="fp-state__icon" aria-hidden="true" />
          <h3>No external events found</h3>
          <p>Check back soon for more events from around the web.</p>
        </div>
      )}

      {!loading.loadBarProgress && visibleEvents.length > 0 && (
        <div className="fp-egrid">
          {visibleEvents.map((item, index) => (
            <div className="fp-cell" key={item.eventid || index}>
              <a
                href={item.url}
                target="_blank"
                rel="noopener noreferrer"
                className="fp-event fp-event--external"
                aria-label={`${item.eventname}, external event`}
                onMouseMove={spot}
              >
                <div className="fp-event__bg">
                  <BsTicketPerforated aria-hidden="true" />
                  <img src={item.poster} alt="" loading="lazy" onError={hidePoster} />
                </div>
                <div className="fp-event__shade" aria-hidden="true" />

                <span className="fp-event__cat">
                  <BsArrowUpRight aria-hidden="true" /> External event
                </span>

                <div className="fp-event__info">
                  <h3 className="fp-event__title">{item.eventname}</h3>
                  <p className="fp-event__place">
                    <BiSolidLocationPlus aria-hidden="true" />
                    <span>{item.location}</span>
                  </p>
                  <p className="fp-event__desc">{plainText(item.description)}</p>
                </div>

                <div className="fp-event__stub">
                  <div className="fp-event__when">
                    <strong>{item.sdate}</strong>
                    {item.stime && (
                      <span>
                        <BsClock aria-hidden="true" /> {item.stime}
                      </span>
                    )}
                  </div>

                  <span className="fp-event__cta">
                    View event <BsArrowUpRight aria-hidden="true" />
                  </span>
                </div>
              </a>
            </div>
          ))}
        </div>
      )}
    </section>
  )
}

export default IndexedEvents
